import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { NativeNavigation } from '../utils/native/native.types';
import { sanitizePath } from '../utils/native/native.utils';
import useIsInWebView from './useIsInWebView';

declare global {
  interface Window {
    __WEBVIEW_BRIDGE__?: {
      NativeNavigation?: NativeNavigation;
    };
    AndroidBridge?: {
      onAccessTokenReceived?: (accessToken: string) => void;
    };
    webkit?: {
      messageHandlers?: {
        iosBridge?: {
          postMessage?: (message: string) => void;
        };
      };
    };
  }
}

const useNativeNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInWebView } = useIsInWebView();

  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);

  navigateRef.current = navigate;
  locationRef.current = location;

  const createBridge = useCallback(
    (): NativeNavigation => ({
      navigateToPath: (path: string) => {
        try {
          const sanitizedPath = sanitizePath(path);
          if (!sanitizedPath) {
            console.warn('Invalid navigation path provided:', path);
            return false;
          }

          navigateRef.current(sanitizedPath);
          return true;
        } catch (error) {
          console.warn('Navigation failed: ', error);
          return false;
        }
      },
      replaceToPath: (path: string) => {
        try {
          const sanitizedPath = sanitizePath(path);
          if (!sanitizedPath) {
            console.warn('Invalid replacement path provided:', path);
            return false;
          }

          navigateRef.current(sanitizedPath, { replace: true });
          return true;
        } catch (error) {
          console.warn('Replace navigation failed: ', error);
          return false;
        }
      },
      goBack: () => {
        try {
          navigateRef.current(-1);
          return true;
        } catch (error) {
          console.warn('Go back navigation failed: ', error);
          return false;
        }
      },
      goForward: () => {
        try {
          navigateRef.current(1);
          return true;
        } catch (error) {
          console.warn('Go forward navigation failed: ', error);
          return false;
        }
      },
      getCurrentPath: () => {
        try {
          const currentLocation = locationRef.current;
          const fullPath =
            currentLocation.pathname +
            currentLocation.search +
            currentLocation.hash;
          return fullPath || '/';
        } catch (error) {
          console.warn('Get current path failed: ', error);
          return '/';
        }
      },
      logout: () => {
        try {
          navigateRef.current('/logout');
          return true;
        } catch (error) {
          console.warn('Logout failed: ', error);
          return false;
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!isInWebView) {
      return;
    }

    const bridge = createBridge();
    const frozenBridge = Object.freeze(bridge);
    if (!window.__WEBVIEW_BRIDGE__) {
      window.__WEBVIEW_BRIDGE__ = {};
    }
    window.__WEBVIEW_BRIDGE__.NativeNavigation = frozenBridge;

    return () => {
      if (window.__WEBVIEW_BRIDGE__?.NativeNavigation === frozenBridge) {
        window.__WEBVIEW_BRIDGE__.NativeNavigation = undefined;
      }
    };
  }, [isInWebView, createBridge]);

  return createBridge;
};

export default useNativeNavigation;
